
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { NewsAlert } from "../types";
import { formatDistanceToNow } from "date-fns";
import { ExternalLink, Eye, EyeOff } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface NewsAlertsTabProps {
  leadId: string;
  newsAlerts: NewsAlert[];
  newsMonitoringEnabled: boolean;
  onUpdate: () => void;
}

export const NewsAlertsTab = ({ 
  leadId, 
  newsAlerts = [], 
  newsMonitoringEnabled,
  onUpdate 
}: NewsAlertsTabProps) => {
  const [isUpdatingMonitoring, setIsUpdatingMonitoring] = useState(false);

  const handleToggleMonitoring = async (enabled: boolean) => {
    setIsUpdatingMonitoring(true);
    
    try {
      const { error } = await supabase
        .from("lead_master")
        .update({ news_monitoring_enabled: enabled })
        .eq("id", leadId);
        
      if (error) throw error;
      
      toast.success(`News monitoring ${enabled ? 'enabled' : 'disabled'}`);
      onUpdate();
      
    } catch (error) {
      console.error("Error updating news monitoring:", error);
      toast.error("Failed to update news monitoring");
    } finally {
      setIsUpdatingMonitoring(false);
    }
  };

  const handleMarkAsRead = async (alertId: string, isRead: boolean) => {
    try {
      const { error } = await supabase
        .from("news_alerts")
        .update({ is_read: isRead })
        .eq("id", alertId);
        
      if (error) throw error;
      
      onUpdate();
      
    } catch (error) {
      console.error("Error updating news alert:", error);
      toast.error("Failed to update news alert");
    }
  };

  const unreadCount = newsAlerts.filter(alert => !alert.is_read).length;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            News Monitoring Settings
            <div className="flex items-center space-x-2">
              <Switch
                id="news-monitoring"
                checked={newsMonitoringEnabled}
                onCheckedChange={handleToggleMonitoring}
                disabled={isUpdatingMonitoring}
              />
              <Label htmlFor="news-monitoring">
                {newsMonitoringEnabled ? 'Enabled' : 'Disabled'}
              </Label>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            {newsMonitoringEnabled 
              ? "Automatically monitoring news and updates about this lead's company and industry."
              : "News monitoring is disabled. Enable it to start tracking relevant news."
            }
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            News Alerts
            {unreadCount > 0 && (
              <Badge variant="secondary">
                {unreadCount} unread
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {newsAlerts.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>No news alerts yet.</p>
              {!newsMonitoringEnabled && (
                <p className="text-sm mt-2">Enable news monitoring to start receiving alerts.</p>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {newsAlerts.map((alert) => (
                <Card key={alert.id} className={`${!alert.is_read ? 'bg-blue-50' : ''}`}>
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-medium line-clamp-2">{alert.title}</h4>
                      <div className="flex items-center gap-2 ml-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleMarkAsRead(alert.id, !alert.is_read)}
                          className="h-8 w-8 p-0"
                        >
                          {alert.is_read ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </Button>
                        {alert.url && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => window.open(alert.url, '_blank')}
                            className="h-8 w-8 p-0"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                    
                    {alert.content && (
                      <p className="text-sm text-muted-foreground line-clamp-3 mb-2">
                        {alert.content}
                      </p>
                    )}
                    
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>{alert.source}</span>
                      <span>
                        {alert.published_at 
                          ? formatDistanceToNow(new Date(alert.published_at), { addSuffix: true })
                          : formatDistanceToNow(new Date(alert.created_at), { addSuffix: true })
                        }
                      </span>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
